import React, { useRef } from 'react';
import { Alert, Box, Button, Chip, Paper, Stack, TextField, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock, faLock, faNoteSticky } from '@fortawesome/pro-solid-svg-icons';
import { useTranslation } from 'react-i18next';

interface CallNotesPanelProps {
    value: string;
    onChange: (value: string) => void;
    disabled?: boolean;
}

/**
 * Interne Gesprächsnotizen — der Block, in den man während eines Telefonats tippt.
 *
 * Bewusst **keine** Markdown-WYSIWYG-Eingabe, anders als bei den Angebotstexten: während jemand
 * am Telefon redet, ist eine Werkzeugleiste im Weg. Ein einfaches, immer sichtbares Textfeld ist
 * hier schneller. Der Inhalt wird als Text gespeichert, Markdown-Syntax bleibt also erhalten und
 * ließe sich später mit dem vorhandenen MarkdownRenderer darstellen, falls es gebraucht wird.
 *
 * Ebenfalls bewusst nicht eingeklappt: eine Notiz, die man erst aufklappen muss, schreibt im
 * Telefonat niemand.
 */
export const CallNotesPanel: React.FC<CallNotesPanelProps> = ({ value, onChange, disabled }) => {
    const { t } = useTranslation();
    const fieldRef = useRef<HTMLTextAreaElement | null>(null);

    /**
     * Setzt „— TT.MM.JJJJ HH:MM —" als neue Zeile ans Ende. Notizen wachsen über mehrere
     * Telefonate; ohne Zeitstempel weiß hinterher niemand, was wann gesagt wurde.
     */
    const addTimestamp = () => {
        const stamp = new Date().toLocaleString('de-DE', {
            day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
        });
        const prefix = value.trim().length > 0 ? `${value.replace(/\s+$/, '')}\n\n` : '';
        onChange(`${prefix}— ${stamp} —\n`);
        // Cursor hinter den Stempel setzen, damit direkt weitergetippt werden kann.
        requestAnimationFrame(() => {
            const el = fieldRef.current;
            if (!el) return;
            el.focus();
            el.selectionStart = el.selectionEnd = el.value.length;
        });
    };

    return (
        <Paper sx={{ p: 2, mb: 2 }}>
            <Stack
                direction="row"
                spacing={1}
                sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', rowGap: 1 }}
            >
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                    <Box sx={{ color: 'primary.main' }}>
                        <FontAwesomeIcon icon={faNoteSticky} />
                    </Box>
                    <Typography variant="h6">{t('calculation.notes.title')}</Typography>
                    <Chip
                        size="small"
                        variant="outlined"
                        icon={<FontAwesomeIcon icon={faLock} style={{ fontSize: '0.75rem' }} />}
                        label={t('calculation.notes.internal')}
                    />
                </Stack>
                <Button
                    size="small"
                    disabled={disabled}
                    onClick={addTimestamp}
                    startIcon={<FontAwesomeIcon icon={faClock} />}
                >
                    {t('calculation.notes.timestamp')}
                </Button>
            </Stack>

            <TextField
                inputRef={fieldRef}
                fullWidth
                multiline
                minRows={4}
                maxRows={16}
                disabled={disabled}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={t('calculation.notes.placeholder') ?? ''}
            />

            <Alert severity="info" variant="outlined" sx={{ mt: 1 }}>
                {t('calculation.notes.hint')}
            </Alert>
        </Paper>
    );
};

export default CallNotesPanel;
